import React, { useMemo } from 'react';
import { Select, Skeleton } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { RegionSelectProps } from './RegionSelect.types';
import { useRegionDropdownQuery } from '../../../../application/queries/regionQueries';

/**
 * RegionSelect Component
 *
 * Dropdown select component for choosing a region.
 * Sprint 032: Uses lightweight dropdown endpoint via React Query
 * instead of full regionStore (12+ fields → 4 fields per region).
 *
 * Features:
 * - Fetches active regions via dedicated dropdown endpoint
 * - Display format: "Region Name (State, Country)"
 * - React Query handles caching (30 min), dedup, and staleness
 * - Loading state with skeleton
 * - Required field validation
 * - Error handling
 *
 * Usage:
 * ```tsx
 * <RegionSelect
 *   value={selectedRegionId}
 *   onChange={handleRegionChange}
 *   label="Select Region"
 *   required
 * />
 * ```
 */
export const RegionSelect: React.FC<RegionSelectProps> = ({
  value,
  onChange,
  label = 'Region',
  description,
  placeholder = 'Select a region...',
  error,
  required = false,
  disabled = false
}): React.ReactElement => {
  const { data: regions, isLoading } = useRegionDropdownQuery();

  // Convert regions to Select data format
  const selectData = useMemo(() => {
    if (!regions) return [];

    return regions.map((region) => {
      // Build location string with only non-null/non-empty values
      const locationParts = [region.state, region.country].filter(Boolean);
      const locationStr = locationParts.length > 0 ? ` (${locationParts.join(', ')})` : '';
      return {
        value: region.id,
        label: `${region.regionName}${locationStr}`
      };
    });
  }, [regions]);

  // Show skeleton while loading
  if (isLoading && (!regions || regions.length === 0)) {
    return <Skeleton height={36} radius="sm" />;
  }

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      data={selectData}
      error={error}
      required={required}
      disabled={disabled}
      leftSection={<IconMapPin size={16} />}
      searchable
      clearable
      maxDropdownHeight={300}
      nothingFoundMessage="No regions found"
      // SESSION_334: Prevent browser autocomplete from filling this with state abbreviations
      autoComplete="off"
      name="region-select"
    />
  );
};
